import AppContext from "PFApp/app_context";
import { CalendarMode, Row } from "PFApp/booking/types";
import { EditMode } from "PFApp/booking/types/edit_mode";
import { RowType } from "PFApp/booking/types/row";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useBookingsForUpdate } from "PFCore/hooks/queries/bookings/bulk_operation/use_bookings_for_update";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { PermissionRule } from "PFTypes";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { UNDETERMINED_FUTURE } from "../../../../../constants/dates";
import { usePastDaysLimit } from "../../../../../hooks";
import { useBookingModuleContext } from "../../../providers/booking_module_context_provider";
import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import { RowMenuItem } from "./row_menu";
import { useRowMenuSelectAllBookingsPayload } from "./use_row_menu_select_all_bookings_payload";

interface UseRowMenuSelectAllBookings {
  row: Row;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  isRowMenuOpened: boolean;
}

type UseRowMenuSelectAllBookingsReturn = {
  selectAllBookings: RowMenuItem;
  isSelectAllBookingsEnabled: boolean;
};

export const useRowMenuSelectAllBookings = ({
  row,
  expanded,
  setExpanded,
  isRowMenuOpened
}: UseRowMenuSelectAllBookings): UseRowMenuSelectAllBookingsReturn => {
  const { setHeaderDisabled } = useBookingModuleContext();
  const { calendarMode } = useBookingsOverviewContext();
  const {
    setEditMode,
    selectEditMode: { changeSelection },
    isEditModeEnabled
  } = useBookingRowsContext();
  const { rowsByKey } = useBookingRowsContext();
  const { utc, formatISODate } = useDateFormatter();
  const pastDaysLimit = usePastDaysLimit();
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const isPermittedTo = useIsPermittedTo(currentProfile);
  const isPermittedToBulkOperations = isPermittedTo(PermissionRule.BulkBookingsOperations);

  const { type } = row;
  const { t } = useTranslation("bookingModule", { keyPrefix: "projects.rowMenu" });
  const { selectionPayload } = useRowMenuSelectAllBookingsPayload({ row });

  const totalSelectionPayload = useMemo(
    () => ({
      ...selectionPayload,
      dateRange: {
        min: formatISODate(utc().subtract(pastDaysLimit, "days")),
        max: formatISODate(UNDETERMINED_FUTURE)
      },
      page: 1,
      perPage: 0
    }),
    [selectionPayload, pastDaysLimit]
  );

  const { data, isLoading } = useBookingsForUpdate("total_selection", totalSelectionPayload, {
    enabled: isRowMenuOpened
  });

  const handleClick = useCallback(() => {
    const isProfileRowInProjectsMode = row.type === RowType.Profile && calendarMode === CalendarMode.Projects;
    if (!expanded && !isProfileRowInProjectsMode) {
      setExpanded(true);
    }
    setHeaderDisabled(true);
    setEditMode(EditMode.Select);
    changeSelection({ row, ...selectionPayload });
  }, [calendarMode, changeSelection, expanded, row, rowsByKey]);

  const isSelectAllBookingsEnabled = useMemo(() => {
    if (isPermittedToBulkOperations && (isLoading || (data?.meta.total ?? 0 > 0))) {
      if (calendarMode === CalendarMode.Projects) {
        return [RowType.Engagement, RowType.Profile, RowType.Role].includes(type);
      }
      if (calendarMode === CalendarMode.Workforce) {
        return [RowType.Engagement, RowType.Role].includes(type);
      }
    }

    return false;
  }, [calendarMode, type, isPermittedToBulkOperations, isLoading, data?.meta.total]);

  return {
    selectAllBookings: {
      id: "selectAllBookingsMode",
      label: t("selectAllBookingsMode"),
      icon: "bulk",
      disabled: isEditModeEnabled,
      item: handleClick,
      isLoading: isLoading
    },
    isSelectAllBookingsEnabled
  };
};
